import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { deleteRequest, downloadBlobGet, fetchTextGet, getRequest } from '../../api/ApiController';
import { DatabaseContext } from '../../redux/DatabaseStore';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { MdSearch, MdSearchOff } from 'react-icons/md';
import { DateTime } from 'luxon';
import { AddIcon, EditIcon, CopyIcon, DeleteIcon, PrintIcon, SearchIcon, ExportIcon, VerticalLineIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon, EditRowIcon, RefreshIcon } from '../svg/svgIcons';
import { PanelMenu } from 'primereact/panelmenu';
import UtilContext from '../../components/UtilContext';
import AuthContext from '../../components/AuthContext';
import { LocaleContext } from '../../components/LocaleContext';
import IntersectionObserverWrapper from '../../components/toolbar/IntersectionObserverWrap';
import { scrollToFirst as scrollToFirstUtil, scrollUp as scrollUpUtil, scrollDown as scrollDownUtil, scrollToLast as scrollToLastUtil } from '../../utils/tabulatorScroll';
import { primeMultiSelectHeaderFilter } from '../../utils/primeMultiSelectHeaderFilter';
import { OverflowMenuProvider } from '../../components/toolbar/OverflowMenu';
import { getEffectiveMask } from '../../utils/effectiveMask';
import { applyUserMaskRowToTabulator, cleanSorters, safeParse } from '../../utils/tabulatorProgressiveLoading';
import { getTabulatorPersistLayoutEvents } from '../../utils/tabulatorPersistLayout';
import { getTabulatorLayoutForStandardMask } from '../../utils/tabulatorLayoutUtils';
import { inputOnEnterHeaderFilter } from '../../utils/inputOnEnterHeaderFilter';
import { dateUserHeaderFilter } from '../../utils/dateUserHeaderFilter';
import { tabulatorColumnMinWidthFromTitle, tabulatorDateUserColumnMinWidth, TABULATOR_LIST_ID_MIN_WIDTH } from '../../utils/tabulatorColumnMinWidth';
import PrintOptionsDialog from '../../components/PrintOptionsDialog';
import { buildExportQueryStringFromParams, openPrintHtmlWindow, triggerBlobDownload } from '../../utils/serviceTaskExport';
import { EXPORT_DOCUMENT_TITLE_KEYS, xlsxDownloadFilename } from '../../utils/exportExcelNaming';
import TabulatorMaskDialog from '../../utils/tabulatorMaskDialog';
import { showExcludedMachinesInLists } from '../../utils/moduleSettings';
import { applyTabulatorRuntimeI18n } from '../../utils/tabulatorRuntimeI18n';

const MACHINE_LIST_COLUMN_I18N_KEYS = Object.freeze({
  id: 'MachineList.Column.Id',
  name: 'MachineList.Column.Name',
  'machine_type.name': 'MachineList.Column.MachineTypeName',
  type: 'MachineList.Column.Type',
  serial_number: 'MachineList.Column.SerialNumber',
  fabrication_year: 'MachineList.Column.FabricationYear',
  last_overhaul: 'MachineList.Column.LastOverhaul',
  description: 'MachineList.Column.Description',
  manufacturer: 'MachineList.Column.Manufacturer',
  manufacturer_address: 'MachineList.Column.ManufacturerAddress',
  manufacturer_tel_number: 'MachineList.Column.ManufacturerTel',
  manufacturer_fax: 'MachineList.Column.ManufacturerFax',
  manufacturer_email: 'MachineList.Column.ManufacturerEmail',
  manufacturer_web: 'MachineList.Column.ManufacturerWeb',
  activity: 'MachineList.Column.Activity',
  created_at: 'MachineList.Column.CreatedAt',
  updated_at: 'MachineList.Column.UpdatedAt',
});

const DEFAULT_COLUMNS_VISIBLE_MACHINE = Object.freeze({
  id: true,
  name: true,
  'machine_type.name': true,
  type: true,
  serial_number: true,
  fabrication_year: true,
  last_overhaul: true,
  description: true,
  manufacturer: true,
  manufacturer_address: true,
  manufacturer_tel_number: true,
  manufacturer_fax: true,
  manufacturer_email: true,
  manufacturer_web: true,
  activity: true,
  created_at: true,
  updated_at: true,
});

function mergeVisibility(saved) {
  const merged = { ...DEFAULT_COLUMNS_VISIBLE_MACHINE };
  if (saved && typeof saved === 'object') {
    Object.keys(DEFAULT_COLUMNS_VISIBLE_MACHINE).forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(saved, k)) merged[k] = saved[k] !== false;
    });
  }
  if (!Object.values(merged).some(Boolean)) return { ...DEFAULT_COLUMNS_VISIBLE_MACHINE };
  return merged;
}

function normalizeMachineColumnVisibilityForSave(stateObj) {
  const out = {};
  for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_MACHINE)) out[field] = stateObj?.[field] !== false;
  return out;
}

function columnsFromTabulatorForStandardMaskSave(raw) {
  const out = {};
  for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_MACHINE)) out[field] = raw?.[field] !== false;
  return out;
}

function clearHeaderFilterForField(table, field) {
  if (!table || field == null) return;
  try { table.setHeaderFilterValue?.(field, '', true); } catch (_) {}
}

function applyDataColumnVisibilityToTable(table, visibilityMap) {
  if (!table) return;
  for (const f of Object.keys(DEFAULT_COLUMNS_VISIBLE_MACHINE)) {
    if (visibilityMap[f] === false) {
      clearHeaderFilterForField(table, f);
      table.hideColumn?.(f);
    } else {
      table.showColumn?.(f);
    }
  }
  setTimeout(() => table.redraw?.(true), 0);
}

function countVisibleDataColumns(vis) {
  if (!vis || typeof vis !== 'object') return 0;
  return Object.keys(DEFAULT_COLUMNS_VISIBLE_MACHINE).filter((k) => vis[k] !== false).length;
}

const normalizeText = (value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/** Hodnoty musia zostať zhodné s `MachineAdd` (select STAV). */
const MACHINE_MASK_FIELD_I18N = Object.freeze({
  id: 'MachineList.Column.Id',
  name: 'MachineList.Column.Name',
  'machine_type.name': 'MachineList.Column.MachineTypeName',
  type: 'MachineList.Column.Type',
  serial_number: 'MachineList.Column.SerialNumber',
  fabrication_year: 'MachineList.Column.FabricationYear',
  last_overhaul: 'MachineList.Column.LastOverhaul',
  description: 'MachineList.Column.Description',
  manufacturer: 'MachineList.Column.Manufacturer',
  manufacturer_address: 'MachineList.Column.ManufacturerAddress',
  manufacturer_tel_number: 'MachineList.Column.ManufacturerTel',
  manufacturer_fax: 'MachineList.Column.ManufacturerFax',
  manufacturer_email: 'MachineList.Column.ManufacturerEmail',
  manufacturer_web: 'MachineList.Column.ManufacturerWeb',
  activity: 'MachineList.Column.Activity',
  created_at: 'MachineList.Column.CreatedAt',
  updated_at: 'MachineList.Column.UpdatedAt',
});

export default function MachineList() {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t, locale } = useContext(LocaleContext);
  const { setDialogText, setVisible, openFavoriteDialog, toast, setFastView, setUserMaskFilter, setStandardMaskFilter, openChosenNotificationPreferences, getDateFormat, formatDate } = useContext(UtilContext);

  const ref = useRef(null);
  const overlayRef = useRef(null);
  const isRestoringRef = useRef(false);
  const submittedSearchValueRef = useRef('');

  const [editIcon, setEditIcon] = useState(true);
  const [deleteIcon, setDeleteIcon] = useState(true);
  const [copyIcon, setCopyIcon] = useState(true);
  const [searchIconColor, setSearchIconColor] = useState('');
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [maskVisible, setMaskVisible] = useState(false);
  const [columnsVisible, setColumnsVisible] = useState({ ...DEFAULT_COLUMNS_VISIBLE_MACHINE });
  const [expandedKeys, setExpandedKeys] = useState({});
  const [printChoiceVisible, setPrintChoiceVisible] = useState(false);
  const [printOrientationChoice, setPrintOrientationChoice] = useState('landscape');
  const [printLayoutChoice, setPrintLayoutChoice] = useState('branded');

  const getTable = useCallback(() => ref.current?.table ?? ref.current, []);
  const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFmt = `${dateFmt} HH:mm`;
  const canSetStandardMask = user?.permissions?.includes('set standard mask');
  const hasApplicableStandardMask = useMemo(() => {
    const row = (state.standard_mask_filters || []).find((m) => m.page === 'MachineList');
    if (!row) return false;
    const attributes = safeParse(row.attributes, []);
    if (!Array.isArray(attributes) || attributes.length <= 1) return false;
    const cols = safeParse(row.columns, {});
    const merged = mergeVisibility(cols && typeof cols === 'object' ? cols : {});
    return Object.values(merged).some(Boolean);
  }, [state.standard_mask_filters]);

  const activityFilterOptions = useMemo(() => [
    { label: t('MachineList.Activity.Active'), value: 'Aktívny' },
    { label: t('MachineList.Activity.Retired'), value: 'Vyradený' },
  ], [t]);

  const parseHeaderMulti = (headerValue) => {
    if (headerValue == null || headerValue === '') return [];
    if (Array.isArray(headerValue)) return headerValue.map((x) => String(x ?? '').trim()).filter(Boolean);
    if (typeof headerValue === 'object' && Array.isArray(headerValue?.value)) return headerValue.value.map((x) => String(x ?? '').trim()).filter(Boolean);
    return String(headerValue).split(/[\s,;|]+/).map((x) => x.trim()).filter(Boolean);
  };

  const textHeaderFilter = useCallback((headerValue, rowValue) => {
    if (!headerValue) return true;
    return normalizeText(rowValue).includes(normalizeText(headerValue));
  }, []);

  const dateOrUserFilter = useCallback((headerValue, rowValue, rowData, filterParams) => {
    if (!headerValue) return true;
    const raw = String(headerValue || '').trim();
    if (raw.startsWith('range:')) {
      const p = raw.slice('range:'.length).split(':');
      if (p.length >= 2 && p[0] && p[1]) {
        const from = DateTime.fromFormat(p[0], 'yyyy-MM-dd').startOf('day');
        const to = DateTime.fromFormat(p[1], 'yyyy-MM-dd').endOf('day');
        const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
        return dt.isValid ? dt >= from && dt <= to : false;
      }
      return true;
    }
    const search = normalizeText(raw);
    const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
    const dateStr = dt.isValid ? `${dt.toFormat(dateFmt)} ${dt.toFormat(dateTimeFmt)} ${dt.toFormat('yyyy-MM-dd')}` : '';
    return normalizeText(dateStr).includes(search);
  }, [dateFmt, dateTimeFmt]);

  const machineTypeOptions = useMemo(() => {
    const loc = locale === 'de' ? 'de' : locale === 'en' || locale === 'gb' ? 'en' : 'sk';
    return (state.machine_types || []).map((mt) => ({ label: mt.name, value: mt.name })).sort((a, b) => (a.label || '').localeCompare(b.label || '', loc));
  }, [locale, state.machine_types]);

  const onSelectRow = useCallback((data) => {
    const count = Array.isArray(data) ? data.length : 0;
    const canEdit = user?.permissions?.includes('edit maintenance');
    const canAdd = user?.permissions?.includes('add maintenance');
    const canDelete = user?.permissions?.includes('delete maintenance');
    setEditIcon(!canEdit || count !== 1);
    setCopyIcon(!canAdd || count !== 1);
    setDeleteIcon(!canDelete || count < 1);
  }, [user?.permissions]);

  const showExcludedMachines = showExcludedMachinesInLists(state.module_settings);

  const machinesListUrl = useMemo(
    () => `/api/machines?show_excluded_machines=${showExcludedMachines ? '1' : '0'}`,
    [showExcludedMachines],
  );

  const reloadMachines = useCallback(() => {
    getRequest(machinesListUrl).then((response) => {
      dispatch({ type: 'load_machines', newMachines: response.data });
    });
  }, [dispatch, machinesListUrl]);

  useEffect(() => {
    reloadMachines();
  }, [reloadMachines]);

  const deleteSelected = useCallback(() => {
    const selected = getTable()?.getSelectedData?.() ?? [];
    Promise.all(selected.map((x) => deleteRequest(`/api/machines/${x.id}`)))
      .then(() => {
        toast?.current?.show({ severity: 'info', summary: t('MachineList.Toast.DeletedSummary'), detail: t('MachineList.Toast.DeletedDetail'), life: 3000 });
        reloadMachines();
      })
      .catch((e) => {
        toast?.current?.show({ severity: 'warn', summary: t('MachineList.Toast.RejectedSummary'), detail: e?.message || t('MachineList.Toast.DeleteFailedDetail'), life: 3000 });
      });
  }, [getTable, reloadMachines, t, toast]);

  const confirmDelete = useCallback(() => {
    confirmDialog({
      message: t('ServiceTask.ConfirmDeleteRecord'),
      header: t('ServiceTask.ConfirmDeleteTitle'),
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('Common.Yes'),
      rejectLabel: t('Common.No'),
      accept: deleteSelected,
    });
  }, [deleteSelected, t]);

  function onEditRow(machine) {
    dispatch({ type: 'create_new_tab', name: t('MachineList.Tab.Edit'), translationKey: 'MachineList.Tab.Edit', legacyLabel: t('MachineList.Tab.Edit'), page: 'MachineAdd', props: { machine, type: 'edit' }, mask: 'gset014' });
  }
      
      function EditRowFormatter(props) {
        const rowData = props.cell.getRow().getData();
    return <div className="flex items-center justify-center w-full h-full"><button type="button" onClick={() => onEditRow(rowData)}><EditRowIcon /></button></div>;
  }

  function CreatedAtCellFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const raw = rowData?.created_at;
    const text = formatDate ? formatDate(raw, dateTimeFmt, true) : DateTime.fromISO(String(raw || '')).toFormat(dateTimeFmt);
    return <div className="flex items-center justify-start w-full h-full min-w-0"><div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div></div>;
  }

  function UpdatedAtCellFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const raw = rowData?.updated_at;
    const text = formatDate ? formatDate(raw, dateTimeFmt, true) : DateTime.fromISO(String(raw || '')).toFormat(dateTimeFmt);
    return <div className="flex items-center justify-start w-full h-full min-w-0"><div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div></div>;
  }

  function TruncatePlainTextFormatter(props) {
    const v = props.cell.getValue();
    const s = v != null ? String(v) : '';
    return <span className="block w-full min-w-0 truncate whitespace-nowrap" title={s || undefined}>{s}</span>;
  }

  const columns = useMemo(() => {
    const colTitle = (key) => t(key);
    return [
      { field: 'rowSelection', formatter: 'rowSelection', titleFormatter: 'rowSelection', hozAlign: 'center', width: 44, headerSort: false, resizable: false, frozen: true, print: false, download: false },
      { title: '', field: 'editor', hozAlign: 'center', width: 44, headerSort: false, formatter: reactFormatter(<EditRowFormatter />), resizable: false, frozen: true, print: false, download: false },
      { title: colTitle('MachineList.Column.Id'), field: 'id', minWidth: TABULATOR_LIST_ID_MIN_WIDTH, widthGrow: 0.6, hozAlign: 'left', sorter: 'number', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter },
      { title: colTitle('MachineList.Column.Name'), field: 'name', hozAlign: 'left', formatter: reactFormatter(<TruncatePlainTextFormatter />), headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('MachineList.Column.Name')), widthGrow: 1.1 },
      {
        title: colTitle('MachineList.Column.MachineTypeName'),
        field: 'machine_type.name',
        hozAlign: 'left',
        formatter: reactFormatter(<TruncatePlainTextFormatter />),
        minWidth: tabulatorColumnMinWidthFromTitle(colTitle('MachineList.Column.MachineTypeName'), 'multiselect'),
        widthGrow: 1,
        headerFilter: primeMultiSelectHeaderFilter,
        headerFilterParams: { placeholder: t('Common.All'), valueType: 'string', options: machineTypeOptions },
        headerFilterFunc: (headerValue, rowValue, rowData) => {
          const selected = parseHeaderMulti(headerValue);
          if (selected.length === 0) return true;
          const machineTypeName = String(rowValue ?? rowData?.machine_type?.name ?? '').trim();
          return selected.includes(machineTypeName);
        },
      },
      { title: colTitle('MachineList.Column.Type'), field: 'type', hozAlign: 'left', formatter: reactFormatter(<TruncatePlainTextFormatter />), headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('MachineList.Column.Type')), widthGrow: 1 },
      { title: colTitle('MachineList.Column.SerialNumber'), field: 'serial_number', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('MachineList.Column.SerialNumber')), widthGrow: 1 },
      { title: colTitle('MachineList.Column.FabricationYear'), field: 'fabrication_year', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('MachineList.Column.FabricationYear')), widthGrow: 0.8 },
      { title: colTitle('MachineList.Column.LastOverhaul'), field: 'last_overhaul', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('MachineList.Column.LastOverhaul')), widthGrow: 1 },
      { title: colTitle('MachineList.Column.Description'), field: 'description', hozAlign: 'left', formatter: reactFormatter(<TruncatePlainTextFormatter />), headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('MachineList.Column.Description')), widthGrow: 1.2 },
      { title: colTitle('MachineList.Column.Manufacturer'), field: 'manufacturer', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('MachineList.Column.Manufacturer')), widthGrow: 1 },
      { title: colTitle('MachineList.Column.ManufacturerAddress'), field: 'manufacturer_address', hozAlign: 'left', formatter: reactFormatter(<TruncatePlainTextFormatter />), headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('MachineList.Column.ManufacturerAddress')), widthGrow: 1.2 },
      { title: colTitle('MachineList.Column.ManufacturerTel'), field: 'manufacturer_tel_number', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('MachineList.Column.ManufacturerTel')), widthGrow: 0.9 },
      { title: colTitle('MachineList.Column.ManufacturerFax'), field: 'manufacturer_fax', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('MachineList.Column.ManufacturerFax')), widthGrow: 0.8 },
      { title: colTitle('MachineList.Column.ManufacturerEmail'), field: 'manufacturer_email', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('MachineList.Column.ManufacturerEmail')), widthGrow: 1.2 },
      { title: colTitle('MachineList.Column.ManufacturerWeb'), field: 'manufacturer_web', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('MachineList.Column.ManufacturerWeb')), widthGrow: 1 },
      {
        title: colTitle('MachineList.Column.Activity'),
        field: 'activity',
        hozAlign: 'left',
        formatter: reactFormatter(<TruncatePlainTextFormatter />),
        minWidth: tabulatorColumnMinWidthFromTitle(colTitle('MachineList.Column.Activity'), 'multiselect'),
        widthGrow: 0.8,
        headerFilter: primeMultiSelectHeaderFilter,
        headerFilterParams: { placeholder: t('Common.All'), valueType: 'string', options: activityFilterOptions },
        headerFilterFunc: (headerValue, rowValue, rowData) => {
          const selected = parseHeaderMulti(headerValue);
          if (selected.length === 0) return true;
          const act = String(rowValue ?? rowData?.activity ?? '').trim();
          return selected.includes(act);
        },
      },
      { title: colTitle('MachineList.Column.CreatedAt'), field: 'created_at', hozAlign: 'left', minWidth: tabulatorDateUserColumnMinWidth(colTitle('MachineList.Column.CreatedAt')), widthGrow: 1, formatter: reactFormatter(<CreatedAtCellFormatter />), headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: dateOrUserFilter, headerFilterFuncParams: { name: 'created_at' }, headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt } },
      { title: colTitle('MachineList.Column.UpdatedAt'), field: 'updated_at', hozAlign: 'left', minWidth: tabulatorDateUserColumnMinWidth(colTitle('MachineList.Column.UpdatedAt')), widthGrow: 1, formatter: reactFormatter(<UpdatedAtCellFormatter />), headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: dateOrUserFilter, headerFilterFuncParams: { name: 'updated_at' }, headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt } },
    ];
  }, [activityFilterOptions, dateFmt, dateOrUserFilter, dateTimeFmt, machineTypeOptions, t, textHeaderFilter]);

  useEffect(() => {
    const table = getTable();
    if (!table?.getColumns) return;
    applyTabulatorRuntimeI18n(table, t, {
      columnTitleKeys: MACHINE_LIST_COLUMN_I18N_KEYS,
      headerFilters: {
        'machine_type.name': { placeholder: t('Common.All'), options: machineTypeOptions },
        activity: { placeholder: t('Common.All'), options: activityFilterOptions },
      },
      reformatRows: true,
    });
  }, [locale, t, getTable, machineTypeOptions, activityFilterOptions]);

  const getMaskColumnLabel = useCallback((field) => {
    const key = MACHINE_MASK_FIELD_I18N[field];
    return key ? t(key) : field;
  }, [t]);

  const getPrintColumnsOrder = useCallback(() => {
    const table = getTable();
    const allowed = Object.keys(DEFAULT_COLUMNS_VISIBLE_MACHINE);
    const fallback = () => allowed.filter((f) => columnsVisible[f] !== false);
    if (!table?.getColumns) return fallback();
    const allowedSet = new Set(allowed);
    const out = [];
    const seen = new Set();
    try {
      table.getColumns().forEach((col) => {
        const field = col.getField?.() ?? col.getDefinition?.()?.field;
        if (!field || !allowedSet.has(field)) return;
        if (columnsVisible[field] === false) return;
        if (seen.has(field)) return;
        seen.add(field);
        out.push(field);
      });
    } catch (_) {
      return fallback();
    }
    return out.length > 0 ? out : fallback();
  }, [getTable, columnsVisible]);

  const parseHeaderFiltersToParams = useCallback(() => {
    const table = getTable();
    const hf = table?.getHeaderFilters?.() ?? [];
    const out = {};
    hf.forEach((f) => {
      if (!f?.field) return;
      const v = f.value;
      if (v == null || String(v).trim() === '') return;
      const keyField = f.field === 'machine_type.name' ? 'machine_type_name' : f.field;
      out[`filter_${keyField}`] = v;
    });
    return out;
  }, [getTable]);

  const buildMachineExportQueryString = useCallback((opts = {}) => {
    const table = getTable();
    const params = {
      ...parseHeaderFiltersToParams(),
      toolbar_search: String(submittedSearchValueRef.current ?? '').trim() || undefined,
      sorters: JSON.stringify(cleanSorters(table?.getSorters?.() ?? [])),
    };
    return buildExportQueryStringFromParams(params, opts);
  }, [getTable, parseHeaderFiltersToParams]);

  const runMachinePrint = useCallback(async () => {
    try {
      setPrintChoiceVisible(false);
      setDialogText(t('Common.PreparingPrint'));
      setVisible(true);
      const suffix = buildMachineExportQueryString({
        includePrintColumns: true,
        printLayout: printLayoutChoice,
        printOrientation: printOrientationChoice,
        printColumns: getPrintColumnsOrder(),
      });
      const html = await fetchTextGet(`/api/machines/export/print${suffix}`);
      if (!openPrintHtmlWindow(html)) {
        toast?.current?.show({ severity: 'warn', summary: t('MachineList.Toast.PrintTitle'), detail: t('Common.EnablePopups'), life: 4000 });
      }
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('MachineList.Toast.PrintTitle'), detail: e?.message || t('MachineList.Toast.PrintFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildMachineExportQueryString, getPrintColumnsOrder, printLayoutChoice, printOrientationChoice, setDialogText, setVisible, t, toast]);

  const exportMachineXlsx = useCallback(async () => {
    try {
      setDialogText(t('Common.PreparingExport'));
      setVisible(true);
      const suffix = buildMachineExportQueryString({
        includePrintColumns: true,
        printColumns: getPrintColumnsOrder(),
      });
      const defaultXlsx = xlsxDownloadFilename(t(EXPORT_DOCUMENT_TITLE_KEYS.MACHINES));
      const { blob, filename } = await downloadBlobGet(`/api/machines/export/xlsx${suffix}`, defaultXlsx);
      triggerBlobDownload(blob, filename || defaultXlsx);
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('MachineList.Toast.ExportTitle'), detail: e?.message || t('MachineList.Toast.ExportFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildMachineExportQueryString, getPrintColumnsOrder, setDialogText, setVisible, t, toast]);

  const closeMenu = () => {
    OverflowMenuProvider.setVisible(null);
    setExpandedKeys({});
  };

  const openMaskDialog = () => {
    setColumnsVisible(mergeVisibility(columnsVisible));
    setMaskVisible(true);
    closeMenu();
  };

  const saveUserMaskColumns = () => {
    const normalized = normalizeMachineColumnVisibilityForSave(columnsVisible);
    if (!Object.values(normalized).some(Boolean)) {
      toast?.current?.show({ severity: 'warn', summary: t('MachineList.Toast.MaskWarnTitle'), detail: t('MachineList.Toast.SelectOneColumn'), life: 4000 });
      return;
    }
    const filtersSnapshot = getTable()?.getHeaderFilters?.() ?? [];
    setUserMaskFilter('MachineList', 'all', normalized, filtersSnapshot, null, null);
    setColumnsVisible(normalized);
    setMaskVisible(false);
  };

  const confirmSaveStandardMask = () => {
    confirmDialog({
      message: t('MachineList.SaveStandardMaskMessage'),
      header: t('MachineList.SaveStandardMaskHeader'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Save'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        const table = getTable();
        const layout = getTabulatorLayoutForStandardMask(table);
        if (!layout) return;
        const columnsForSave = columnsFromTabulatorForStandardMaskSave(layout.columns);
        setStandardMaskFilter('MachineList', columnsForSave, layout.attributes);
      },
    });
  };

  const confirmApplyStandardLayoutToUserMask = () => {
    closeMenu();
    const standard = (state.standard_mask_filters || []).find((m) => m.page === 'MachineList');
    if (!standard) {
      toast?.current?.show({ severity: 'warn', summary: t('MachineList.Toast.StandardMaskWarnTitle'), detail: t('MachineList.StandardMaskMissing'), life: 4000 });
      return;
    }
    const attributes = safeParse(standard.attributes, []);
    if (!Array.isArray(attributes) || attributes.length <= 1) return;
    const jsonColumns = safeParse(standard.columns, {});
    const mergedCols = mergeVisibility(jsonColumns && typeof jsonColumns === 'object' ? jsonColumns : {});
    const colsForSave = normalizeMachineColumnVisibilityForSave(mergedCols);
    confirmDialog({
      message: t('MachineList.ApplyStandardMaskMessage'),
      header: t('MachineList.ApplyStandardMaskHeader'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Apply'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        setUserMaskFilter('MachineList', 'all', colsForSave, null, attributes, null, () => {
          const table = getTable();
          if (!table) return;
          isRestoringRef.current = true;
          try { table.setColumnLayout?.(attributes); } catch (_) {}
          setColumnsVisible(colsForSave);
          applyDataColumnVisibilityToTable(table, colsForSave);
          setTimeout(() => { isRestoringRef.current = false; }, 0);
        });
      },
    });
  };

  function setMask(field, value) {
    if (!Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE_MACHINE, field)) return;
    const table = getTable();
    const next = { ...DEFAULT_COLUMNS_VISIBLE_MACHINE, ...columnsVisible, [field]: value };
    if (value === false && countVisibleDataColumns(next) < 1) {
      toast?.current?.show({ severity: 'warn', summary: t('MachineList.Toast.MaskWarnTitle'), detail: t('MachineList.Toast.AtLeastOneColumn'), life: 3500 });
      return;
    }
    applyDataColumnVisibilityToTable(table, next);
    setColumnsVisible(next);
  }

  function maskSelectAll() {
    const next = { ...DEFAULT_COLUMNS_VISIBLE_MACHINE };
    Object.keys(next).forEach((k) => { next[k] = true; });
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  function maskDeselectAll() {
    const next = {};
    Object.keys(DEFAULT_COLUMNS_VISIBLE_MACHINE).forEach((k) => { next[k] = false; });
    next.id = true;
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  const itemRenderer = (item, options) => (
    <button type="button" className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px] border-0 text-left" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </button>
  );

  const items = useMemo(() => [{
    key: '0',
    label: t('Common.Actions'),
    icon: 'pi pi-sliders-v',
    template: itemRenderer,
    items: [
      { key: '0_1', label: t('Common.SetFilter'), icon: 'pi pi-filter', command: () => { setUserMaskFilter('MachineList', 'set_filters', null, getTable()?.getHeaderFilters?.() ?? [], null); closeMenu(); }, template: itemRenderer },
      { key: '0_2', label: t('Common.ClearFilter'), icon: 'pi pi-filter-slash', command: () => { setUserMaskFilter('MachineList', 'set_filters', null, [], null); getTable()?.clearHeaderFilter?.(); closeMenu(); }, template: itemRenderer },
      { key: '0_3', label: t('Common.SetSorting'), icon: 'pi pi-sort-alt', command: () => { setUserMaskFilter('MachineList', 'set_sorters', null, null, null, cleanSorters(getTable()?.getSorters?.() ?? [])); closeMenu(); }, template: itemRenderer },
      { key: '0_4', label: t('Common.ClearSorting'), icon: 'pi pi-sort-alt-slash', command: () => { setUserMaskFilter('MachineList', 'set_sorters', null, null, null, []); getTable()?.setSort?.([]); closeMenu(); }, template: itemRenderer },
      { key: '0_5', label: t('Common.SetMask'), icon: 'pi pi-server', command: openMaskDialog, template: itemRenderer },
      ...(hasApplicableStandardMask ? [{ key: '0_5s', label: t('Common.StandardMask'), icon: 'pi pi-clone', command: confirmApplyStandardLayoutToUserMask, template: itemRenderer }] : []),
      { key: '0_6', label: t('ServiceTask.Notifications'), icon: 'pi pi-envelope', command: () => { openChosenNotificationPreferences('MachineList'); closeMenu(); }, template: itemRenderer },
    ],
  }], [closeMenu, confirmApplyStandardLayoutToUserMask, getTable, hasApplicableStandardMask, openChosenNotificationPreferences, openMaskDialog, setUserMaskFilter, t]);

  const handleClickOutside = (event) => {
    if (overlayRef.current && !overlayRef.current.contains(event.target)) setExpandedKeys({});
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  const searchTable = () => {
    const normalized = String(searchValue ?? '').trim();
    submittedSearchValueRef.current = normalized;
    setSearchIconColor(normalized === '' ? '' : 'active');
    const table = getTable();
    if (!table) return;
    if (normalized === '') table.clearFilter(true);
    else {
      const needle = normalizeText(normalized);
      table.setFilter((data) => normalizeText(JSON.stringify(data)).includes(needle));
    }
    setVisibleSearch(false);
  };

  const scrollToFirst = () => scrollToFirstUtil(getTable());
  const scrollUp = () => scrollUpUtil(getTable());
  const scrollDown = () => scrollDownUtil(getTable());
  const scrollToLast = () => scrollToLastUtil(getTable());

  return (
    <>
      <div className="w-full h-10 flex flex-row bg-[#333]">
        <IntersectionObserverWrapper>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="add"><button type="button" className="disabled:opacity-25" disabled={!user?.permissions?.includes('add maintenance')} onClick={() => dispatch({ type: 'create_new_tab', name: t('MachineList.Tab.New'), translationKey: 'MachineList.Tab.New', legacyLabel: t('MachineList.Tab.New'), page: 'MachineAdd', props: { machine: null, type: 'add' }, mask: 'gset014' })}><AddIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="edit"><button type="button" className="disabled:opacity-25" disabled={editIcon} onClick={() => dispatch({ type: 'create_new_tab', name: t('MachineList.Tab.Edit'), translationKey: 'MachineList.Tab.Edit', legacyLabel: t('MachineList.Tab.Edit'), page: 'MachineAdd', props: { machine: getTable()?.getSelectedData?.()?.[0], type: 'edit' }, mask: 'gset014' })}><EditIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="copy"><button type="button" className="disabled:opacity-25" disabled={copyIcon} onClick={() => dispatch({ type: 'create_new_tab', name: t('MachineList.Tab.New'), translationKey: 'MachineList.Tab.New', legacyLabel: t('MachineList.Tab.New'), page: 'MachineAdd', props: { machine: getTable()?.getSelectedData?.()?.[0], type: 'copy' }, mask: 'gset014' })}><CopyIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="delete"><button type="button" className="disabled:opacity-25" onClick={confirmDelete} disabled={deleteIcon}><DeleteIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="print"><button type="button" onClick={() => setPrintChoiceVisible(true)}><PrintIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="search">
            <button type="button" title={searchIconColor !== 'active' ? t('Common.Search') : t('ServiceTask.CancelSearch')} onClick={searchIconColor !== 'active' ? () => setVisibleSearch(true) : () => { setSearchIconColor(''); setSearchValue(''); submittedSearchValueRef.current = ''; getTable()?.clearFilter?.(true); }}>
              {searchIconColor === '' ? <MdSearch fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" /> : <MdSearchOff fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" />}
            </button>
          </div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="export"><button type="button" onClick={exportMachineXlsx} title={t('Common.ExportToExcel')}><ExportIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="refresh"><button type="button" onClick={reloadMachines}><RefreshIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="first"><button type="button" onClick={scrollToFirst} title={t('Icon.First')}><BackToFirstIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="back"><button type="button" onClick={scrollUp} title={t('Icon.Previous')}><BackIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="forward"><button type="button" onClick={scrollDown} title={t('Icon.Next')}><ForwardIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="last"><button type="button" onClick={scrollToLast} title={t('Icon.Last')}><ForwardToLastIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="favorite"><button type="button" onClick={() => openFavoriteDialog(t('MachineList.FavoriteTitle'), 'MachineList')}><FavoriteIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="fast"><button type="button" onClick={() => setFastView('MachineList', columnsVisible, columns)}><PriceIcon /></button></div>
          {canSetStandardMask ? (
            <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="save-standard-mask">
              <button type="button" title={t('Common.SaveStandardMask')} aria-label={t('Common.SaveStandardMask')} onClick={confirmSaveStandardMask} className="flex items-center justify-center">
                <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
              </button>
            </div>
          ) : null}
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
        <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
          </div>
          </IntersectionObserverWrapper>
      </div>

      <div className="px-1 bg-white rounded-md shadow-md">
<div className="flex flex-col overflow-x-auto no-scrollbar min-h-[calc(100vh-177px)]">
<div className="overflow-hidden">
            <div className="inline-block w-full py-2">
      <ReactTabulator
                key="machine-list"
                data={state.machines}
                className="h-[calc(100vh_-_193px)]"
                onRef={(r) => { ref.current = r?.current; }}
                columns={columns}
      events={{
        rowSelectionChanged: onSelectRow,
        tableBuilt: () => {
                    const table = getTable();
                    if (!table) return;
                    const effective = getEffectiveMask('MachineList', state);
                    if (effective?.type === 'user' && effective.userMask) {
                      const m = effective.userMask;
                      isRestoringRef.current = true;
                      applyUserMaskRowToTabulator(table, m, {
                        setColumnsVisible: (v) => setColumnsVisible(mergeVisibility(v)),
                        clearHeaderFiltersWhenEmpty: true,
                        applyColumnVisibilityFn: (tbl, jc, setVis) => {
                          const merged = mergeVisibility(jc);
                          setVis(merged);
                          applyDataColumnVisibilityToTable(tbl, merged);
                        },
                      });
                      {
                        const merged = mergeVisibility(safeParse(m.columns, {}));
                        for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_MACHINE)) {
                          if (merged[field] === false) clearHeaderFilterForField(table, field);
                        }
                      }
                      setTimeout(() => { isRestoringRef.current = false; }, 0);
                    } else if (effective?.type === 'standard' && effective.standardMask) {
                      const m = effective.standardMask;
                      isRestoringRef.current = true;
                      const attributes = safeParse(m.attributes, []);
                      const jsonColumns = safeParse(m.columns, {});
                      if (Array.isArray(attributes) && attributes.length > 1) table.setColumnLayout?.(attributes);
                      if (jsonColumns && typeof jsonColumns === 'object') {
                        const merged = mergeVisibility(jsonColumns);
                        setColumnsVisible(merged);
                        applyDataColumnVisibilityToTable(table, merged);
                      }
                      setTimeout(() => { isRestoringRef.current = false; }, 0);
                    } else {
                      applyDataColumnVisibilityToTable(table, DEFAULT_COLUMNS_VISIBLE_MACHINE);
                    }
                  },
                  ...getTabulatorPersistLayoutEvents('MachineList', ref, setUserMaskFilter),
                }}
                options={{ renderHorizontal: 'virtual', layout: 'fitColumns', movableColumns: true, autoResize: true, headerSortTristate: true, filterMode: 'local' }}
              />
    </div> 
  </div> 
</div> 
</div>

      <PrintOptionsDialog
        visible={printChoiceVisible}
        onHide={() => setPrintChoiceVisible(false)}
        orientation={printOrientationChoice}
        onOrientationChange={setPrintOrientationChoice}
        layout={printLayoutChoice}
        onLayoutChange={setPrintLayoutChoice}
        onConfirm={runMachinePrint}
      />

      <Dialog header={t('MachineList.SearchDialogTitle')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
        <div className="flex flex-col">
          <p className="text-sm text-gray-600 mb-2">{t('MachineList.SearchHint')}</p>
          <div className="flex items-center gap-3 w-full">
            <input value={searchValue} onChange={(e) => setSearchValue(e.target.value)} type="text" className="flex-1 h-10 px-4 border rounded-md focus:border-gray-400" />
            <button type="button" onClick={searchTable} className="h-10 w-16 bg-zinc-900 text-white rounded-md hover:bg-zinc-800 flex items-center justify-center"><SearchIcon /></button>
            </div>
        </div>
      </Dialog>

      <TabulatorMaskDialog
        visible={maskVisible}
        onHide={() => setMaskVisible(false)}
        title={t('MachineList.MaskDialogTitle')}
        fields={Object.keys(DEFAULT_COLUMNS_VISIBLE_MACHINE)}
        checkedMap={columnsVisible}
        onToggleField={setMask}
        onSelectAll={maskSelectAll}
        onDeselectAll={maskDeselectAll}
        onSave={saveUserMaskColumns}
        selectAllLabel={t('MachineList.MaskSelectAll')}
        deselectAllLabel={t('MachineList.MaskDeselectAll')}
        saveLabel={t('MachineList.MaskSave')}
        getFieldLabel={getMaskColumnLabel}
        inputIdPrefix="machine-mask"
      />
        </>
  );
}